import { useState } from "react";
import { actualSlotMs, planWith } from "../../../../engine/lookahead";
import { dayPlan } from "../../../../engine/plan";
import { DEFAULT_CONFIG, MINUTE, NOTICE_MAX, NOTICE_MS } from "../../../../engine/state";
import type { Config } from "../../../../engine/types";
import { wallNotice } from "../../../../shared/day";
import { clockTime, type TabProps } from "./common";

/** One screen in the hall: on, gone for how long, or never seen. */
function ScreenBlock({ label, count, gone, now, closed }: { label: string; count: number; gone: number | undefined; now: number; closed?: boolean }) {
  const mins = gone === undefined ? 0 : Math.floor((now - gone) / MINUTE);
  const state = count > 0 ? "on" : closed ? "closed" : gone === undefined ? "none" : "gone";
  const text = count > 1
    ? `${count} on`
    : count === 1
      ? "On"
      : closed
        ? "Not in use"
        : gone === undefined
          ? "Not seen yet"
          : mins < 1 ? "Gone just now" : `Gone ${mins} min`;
  return (
    <li className={"ad-screen " + state}>
      <b>{label}</b>
      <span>{text}</span>
    </li>
  );
}

/** The screens strip: which screens in the hall are connected, as the server last said. */
export function ScreensStrip({ t, state }: TabProps) {
  const screens = t.screens;
  const now = t.now();
  return (
    <section className="ad-card">
      <h2>Screens</h2>
      {screens ? (
        <>
          <ul className="ad-screens">
            <ScreenBlock label="Projector" count={screens.projector} gone={screens.gone.projector} now={now} />
            {screens.courts.map((n, i) => (
              <ScreenBlock key={i} label={`Court ${i + 1}`} count={n} gone={screens.gone[`court-${i + 1}`]} now={now}
                closed={i + 1 > state.config.courts} />
            ))}
            <ScreenBlock label="Watcher" count={screens.watcher} gone={screens.gone.watcher} now={now} />
          </ul>
          <p className="ad-note">
            <b>{screens.players}</b> {screens.players === 1 ? "phone" : "phones"}, <b>{screens.admin}</b> {screens.admin === 1 ? "admin" : "admins"}
          </p>
        </>
      ) : (
        <p className="ad-note">Waiting to hear which screens are on.</p>
      )}
    </section>
  );
}

/** Minutes to the nearest half, without a trailing ".0". */
const mins = (ms: number): string => String(Math.round((ms / MINUTE) * 2) / 2);

/** The plan (decision 35), and the two shorter games to fall back on when the day runs late. */
const PLAN = { gameMs: DEFAULT_CONFIG.gameMs, changeoverMs: DEFAULT_CONFIG.changeoverMs };
const SHORTER_GAMES = [10 * MINUTE, 8 * MINUTE];

type SlotPatch = Partial<Pick<Config, "gameMs" | "changeoverMs">>;

/** The pace line, and what the plan and shorter games would each buy. */
export function Pace({ t, state }: TabProps) {
  const [open, setOpen] = useState(false);
  const now = t.now();
  const { gameMs, changeoverMs, lastWhistleAt } = state.config;
  const plan = gameMs + changeoverMs;
  const actual = actualSlotMs(state);
  // over plan as the line below words it, so the line and the choices never disagree
  const over = actual !== null && Number(mins(actual)) > Number(mins(plan));
  const left = dayPlan(state, now).roundsLeft;
  const rounds = (n: number): string => `${n} more ${n === 1 ? "round" : "rounds"}`;

  const planName = `${mins(PLAN.gameMs)} and ${mins(PLAN.changeoverMs)}`;
  const onPlan = gameMs === PLAN.gameMs && changeoverMs === PLAN.changeoverMs;
  // the one in use is marked, so what the others buy can be read against it
  const choices: { label: string; patch: SlotPatch; on: boolean }[] = [
    { label: onPlan ? `${planName} (the plan)` : `Back to ${planName}`, patch: PLAN, on: onPlan },
    ...SHORTER_GAMES.map((ms) => ({ label: `${mins(ms)} minute games`, patch: { gameMs: ms }, on: gameMs === ms })),
  ];

  return (
    <section className="ad-card">
      <h2>The pace</h2>
      <p className="ad-pace">
        {actual !== null
          ? `Rounds are taking ${mins(actual)} min (plan ${mins(plan)}).`
          : `Rounds are planned at ${mins(plan)} min each.`}{" "}
        {lastWhistleAt !== null
          ? `${rounds(left)} fit before ${clockTime(lastWhistleAt)}.`
          : "Set the last whistle on the Rounds tab to see how many fit."}
      </p>
      {over || open ? (
        <>
          <div className="ad-stack">
            {choices.map(({ label, patch, on }) => (
              <button key={label} type="button" className={"btn ad-wide ad-choice" + (on ? " on" : "")} aria-pressed={on}
                onClick={() => { if (!on) t.send({ type: "config.updated", patch }); setOpen(false); }}>
                <span>{label}</span>
                {lastWhistleAt !== null && <span className="ad-choice-buys">{rounds(planWith(state, now, patch).roundsLeft)}</span>}
              </button>
            ))}
          </div>
          <p className="ad-note">They apply from the next round.</p>
          {!over && <button type="button" className="btn quiet ad-wide" onClick={() => setOpen(false)}>Leave it as it is</button>}
        </>
      ) : (
        <button type="button" className="btn quiet ad-wide" onClick={() => setOpen(true)}>Change the pace</button>
      )}
    </section>
  );
}

const READY = ["Food is ready", "Everyone to the wall please", "Five minute break"];

/** A message for the wall: typed or one of three ready ones, and the way to take it down. */
export function WallMessage({ t, state }: TabProps) {
  const [text, setText] = useState("");
  const now = t.now();
  const notice = wallNotice(state, now);
  const clean = text.trim();

  function show(words: string) {
    t.send({ type: "wall.message", text: words });
    setText("");
  }

  return (
    <section className="ad-card">
      <h2>Wall message</h2>
      {notice && (
        <div className="ad-notice" role="status">
          <span className="ad-notice-label">
            {notice.untilResume ? "On the wall until you resume" : `On the wall now, ${Math.max(1, Math.ceil((NOTICE_MS - (now - notice.at)) / 1000))} s left`}
          </span>
          <b>{notice.text}</b>
          <button type="button" className="btn warn ad-wide" onClick={() => t.send({ type: "wall.message", text: null })}>Take it down</button>
        </div>
      )}
      <form className="ad-stack" onSubmit={(e) => { e.preventDefault(); if (clean) show(clean); }}>
        <label className="ad-field">
          <span className="ad-field-top"><span>Your words</span><span className="ad-counter">{text.length} of {NOTICE_MAX}</span></span>
          <input value={text} maxLength={NOTICE_MAX} placeholder="Type a message" autoComplete="off" onChange={(e) => setText(e.target.value)} />
        </label>
        <button type="submit" className="btn accent ad-wide" disabled={!clean}>Show it on the wall</button>
      </form>
      <h3>One tap</h3>
      <div className="ad-stack">
        {READY.map((words) => (
          <button key={words} type="button" className="btn ad-wide" onClick={() => show(words)}>{words}</button>
        ))}
      </div>
      <p className="ad-note">
        {state.pausedAll
          ? "While everything is paused it stays on the pause screen until you resume or take it down."
          : `It stays up for ${NOTICE_MS / 1000} seconds, then goes by itself.`}
      </p>
    </section>
  );
}
